<!-- src/views/employee/GetTheApp.vue - Android app download page -->
<template>
  <div class="ep-page max-w-6xl">

    <!-- ── Hero ───────────────────────────────────────────── -->
    <div class="rounded-2xl overflow-hidden relative"
         style="background: linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(124,58,237,0.14) 100%); border: 1px solid rgba(129,140,248,0.28)">
      <div class="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
           style="background: radial-gradient(circle, rgba(129,140,248,0.16) 0%, transparent 70%)"></div>

      <div class="relative px-5 sm:px-8 py-7">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-4">
            <div class="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                 style="background: rgba(99,102,241,0.25); border: 1px solid rgba(129,140,248,0.4)">
              <Smartphone class="w-7 h-7 text-indigo-200" />
            </div>
            <div>
              <h1 class="text-xl sm:text-2xl font-bold text-white">{{ t('empGetApp.title') }}</h1>
              <p class="text-sm text-indigo-200/70 mt-0.5">{{ t('empGetApp.subtitle') }}</p>
            </div>
          </div>
          <button @click="$router.back()" class="ep-btn ep-btn-secondary ep-btn-sm shrink-0">
            <ArrowLeft class="w-4 h-4" />
            <span class="hidden sm:inline">{{ t('empGetApp.back') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Download + QR ──────────────────────────────────── -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

      <!-- Download card -->
      <div class="ep-card p-5 sm:p-6 flex flex-col">
        <div class="flex items-center gap-2.5 mb-5">
          <Download class="w-5 h-5 text-indigo-400" />
          <h2 class="text-base font-semibold text-slate-100">{{ t('empGetApp.downloadAndroid') }}</h2>
        </div>

        <a :href="apkUrl" download="ExoTrack.apk"
           class="ep-btn ep-btn-primary w-full !py-3.5 !text-base justify-center">
          <Download class="w-5 h-5" />
          {{ t('empGetApp.downloadBtn') }}{{ appVersion }}
        </a>

        <div class="mt-5 space-y-2.5">
          <div class="flex items-center justify-between text-sm">
            <span class="text-slate-500">{{ t('empGetApp.version') }}</span>
            <span class="font-semibold text-slate-200">{{ appVersion }}</span>
          </div>
          <div class="flex items-center justify-between text-sm">
            <span class="text-slate-500">{{ t('empGetApp.size') }}</span>
            <span class="font-semibold text-slate-200">{{ t('empGetApp.sizeValue') }}</span>
          </div>
          <div class="flex items-center justify-between text-sm">
            <span class="text-slate-500">{{ t('empGetApp.requires') }}</span>
            <span class="font-semibold text-slate-200">{{ t('empGetApp.requiresValue') }}</span>
          </div>
        </div>

        <div class="mt-auto pt-5">
          <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{{ t('empGetApp.checksum') }}</p>
          <code class="block text-[11px] text-indigo-300 break-all rounded-lg px-3 py-2"
                style="background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.18)">{{ sha256 }}</code>
        </div>
      </div>

      <!-- QR card -->
      <div class="ep-card p-5 sm:p-6 flex flex-col items-center justify-center text-center">
        <div class="flex items-center gap-2.5 mb-5 self-start">
          <QrCode class="w-5 h-5 text-indigo-400" />
          <h2 class="text-base font-semibold text-slate-100">{{ t('empGetApp.scanQr') }}</h2>
        </div>
        <div class="bg-white rounded-2xl p-4 shadow-2xl">
          <img :src="qrSvg" :alt="t('empGetApp.qrAlt')" class="w-44 h-44 sm:w-52 sm:h-52" />
        </div>
        <p class="text-xs text-slate-500 mt-4 max-w-xs leading-relaxed">
          {{ t('empGetApp.qrHint') }}
        </p>
      </div>
    </div>

    <!-- ── Install steps ──────────────────────────────────── -->
    <div class="ep-card p-5 sm:p-6">
      <div class="flex items-center gap-2.5 mb-5">
        <ListChecks class="w-5 h-5 text-emerald-400" />
        <h2 class="text-base font-semibold text-slate-100">{{ t('empGetApp.howToInstall') }}</h2>
      </div>

      <div class="space-y-3">
        <div v-for="(step, i) in installSteps" :key="i"
             class="flex items-start gap-3.5 rounded-xl p-4"
             style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
          <div class="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold text-indigo-300"
               style="background: rgba(99,102,241,0.15); border: 1px solid rgba(99,102,241,0.3)">
            {{ i + 1 }}
          </div>
          <div class="min-w-0">
            <p class="text-sm font-semibold text-slate-200">{{ step.title }}</p>
            <p class="text-xs text-slate-500 mt-0.5 leading-relaxed">{{ step.detail }}</p>
          </div>
        </div>
      </div>

      <div class="mt-4 rounded-xl p-4 flex items-start gap-3"
           style="background: rgba(251,191,36,0.05); border: 1px solid rgba(251,191,36,0.2)">
        <Info class="w-[1.125rem] h-[1.125rem] text-amber-400 shrink-0 mt-0.5" />
        <p class="text-xs text-slate-400 leading-relaxed">
          The app is distributed directly by your company, not through the Play Store, so Android shows the
          warnings above for any app installed this way. They are expected, nothing is wrong with your phone
          or the app. Future updates install right over the existing app, no uninstall needed.
        </p>
      </div>
    </div>

  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Smartphone,
  ArrowLeft,
  Download,
  QrCode,
  ListChecks,
  Info,
} from 'lucide-vue-next';
import qrSvg from '@/assets/apk-qr.svg';

const { t } = useI18n();

const apkUrl = '/downloads/ExoTrack.apk';
const appVersion = '1.2.2';
const sha256 = '724f84095418b42efe069dd8f5dc9ffe42736e006214166cd65d0c98e30d982c';

const installSteps = computed(() => [
  { title: t('empGetApp.step1Title'), detail: t('empGetApp.step1Desc') },
  { title: t('empGetApp.step2Title'), detail: t('empGetApp.step2Desc') },
  { title: t('empGetApp.step3Title'), detail: t('empGetApp.step3Desc') },
  { title: t('empGetApp.step4Title'), detail: t('empGetApp.step4Desc') },
  { title: t('empGetApp.step5Title'), detail: t('empGetApp.step5Desc') },
]);
</script>
